import { pluralizeWithCount } from "@common/helpers/string";
import { User } from "@common/types/user";
import { FilterMultiselectOption } from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { formatFilterLabelText } from "@components/pageHeader/filters/ActiveFilters/formatFilterLabel";

export const UNKNOWN_USER_LABEL = "Unknown user";

type MemberUser = Pick<User, "name" | "preferredName" | "email">;

interface Member {
    user: MemberUser & Pick<User, "id">;
}

export type MemberNameFn = (user: MemberUser) => string;

export const getMemberDisplayName: MemberNameFn = (user) =>
    user.preferredName ?? user.name;

export function buildWorkspaceMemberFilterOptions({
    members,
    selectedUserIds,
    getName = getMemberDisplayName,
    section,
}: {
    members: Member[];
    selectedUserIds: number[] | undefined;
    getName?: MemberNameFn;
    section?: string;
}): FilterMultiselectOption[] {
    const options: FilterMultiselectOption[] = members.map((member) => {
        const name = getName(member.user);
        return {
            value: member.user.id,
            label: name,
            searchableLabel: `${name} ${member.user.email ?? ""}`,
            section,
        };
    });

    // Keep a selected user who left the workspace visible and filterable.
    const memberIds = new Set(members.map((member) => member.user.id));
    for (const id of selectedUserIds ?? []) {
        if (memberIds.has(id)) {
            continue;
        }
        options.push({
            value: id,
            label: UNKNOWN_USER_LABEL,
            searchableLabel: UNKNOWN_USER_LABEL,
            section,
        });
    }

    return options;
}

export function formatWorkspaceMemberFilterLabel({
    userIds,
    members,
    getName = getMemberDisplayName,
}: {
    userIds: number[] | undefined;
    members: Member[] | undefined;
    getName?: MemberNameFn;
}): string | null {
    if (!userIds?.length) {
        return null;
    }

    // Members are still loading or failed to load: show a count instead of
    // flashing every selected user as unknown or rendering a blank chip.
    if (!members) {
        return pluralizeWithCount("user", userIds.length);
    }

    const nameByUserId = new Map(
        members.map((member) => [member.user.id, getName(member.user)]),
    );
    return formatFilterLabelText(
        userIds.map((id) => nameByUserId.get(id) ?? UNKNOWN_USER_LABEL),
    );
}
